import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact, ExactArrayItems } from "@Helpers/types/exact";
import type { FolderItem, SelectedFileItem, BreadcrumbItem } from "@ServerTypes/folders/folders.schema";

export type { FolderItem, SelectedFileItem, BreadcrumbItem };

const refreshFileManagerSlice = createSlice({
  name: "refreshFileManager",
  initialState: false,
  reducers: {
    setRefreshFileManager: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

const selectedFilesSlice = createSlice({
  name: "selectedFiles",
  initialState: [] as SelectedFileItem[],
  reducers: {
    setSelectedFiles: (_state: SelectedFileItem[], action: PayloadAction<SelectedFileItem[]>) => action.payload,
    emptySelectedFiles: (): SelectedFileItem[] => [],
  },
});

const currentFolderSlice = createSlice({
  name: "currentFolder",
  initialState: null as FolderItem | null,
  reducers: {
    setCurrentFolder: (_state: FolderItem | null, action: PayloadAction<FolderItem | null>) => {
      const payload = action.payload;
      return payload ? { ...payload } : null;
    },
    emptyCurrentFolder: () => null,
  },
});

const fileManagerBreadCrumbSlice = createSlice({
  name: "fileManagerBreadCrumb",
  initialState: [] as BreadcrumbItem[],
  reducers: {
    setFileManagerBreadCrumbData: (_state: BreadcrumbItem[], action: PayloadAction<BreadcrumbItem[]>) => action.payload,
  },
});

export const { setRefreshFileManager } = refreshFileManagerSlice.actions;

const { setSelectedFiles: _setSelectedFilesRaw } = selectedFilesSlice.actions;
const { setFileManagerBreadCrumbData: _setFileManagerBreadCrumbDataRaw } = fileManagerBreadCrumbSlice.actions;
const { setCurrentFolder: _setCurrentFolderRaw } = currentFolderSlice.actions;
export const { emptyCurrentFolder } = currentFolderSlice.actions;
export const { emptySelectedFiles } = selectedFilesSlice.actions;

/**
 * Object- and array-payload action creators wrapped with `Exact<>` /
 * `ExactArrayItems<>` so excess properties are caught even when the payload
 * comes through a variable. Array wraps run per-element checking.
 */
export const setCurrentFolder = <T extends Exact<FolderItem | null, T>>(payload: T) => _setCurrentFolderRaw(payload);
export const setSelectedFiles = <T extends readonly SelectedFileItem[]>(
  payload: T & ExactArrayItems<SelectedFileItem, T>,
) => _setSelectedFilesRaw([...payload]);
export const setFileManagerBreadCrumbData = <T extends readonly BreadcrumbItem[]>(
  payload: T & ExactArrayItems<BreadcrumbItem, T>,
) => _setFileManagerBreadCrumbDataRaw([...payload]);

export const refreshFileManager = refreshFileManagerSlice.reducer;
export const selectedFiles = selectedFilesSlice.reducer;
export const currentFolder = currentFolderSlice.reducer;
export const fileManagerBreadCrumb = fileManagerBreadCrumbSlice.reducer;
